import { Card, Select, Input, Button, Typography, Space, Tag, Tabs, message } from 'antd'
import { PlayCircleOutlined, CopyOutlined, HistoryOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'

const { Title, Text } = Typography
const { Option } = Select
const { TextArea } = Input
const { TabPane } = Tabs

const FallbackAppApiPlayground = () => {
  const navigate = useNavigate()
  const [method, setMethod] = useState('GET')
  const [endpoint, setEndpoint] = useState('/api/v1/demo/health')
  const [requestBody, setRequestBody] = useState('')
  const [response, setResponse] = useState('')
  const [loading, setLoading] = useState(false)

  const methodColors: Record<string, string> = {
    GET: 'green',
    POST: 'blue',
    PUT: 'orange',
    DELETE: 'red',
    PATCH: 'purple'
  }

  const sampleEndpoints = [
    { method: 'GET', path: '/api/v1/demo/health', description: 'Health check' },
    { method: 'POST', path: '/api/v1/demo/query', description: 'Execute query' },
    { method: 'GET', path: '/api/v1/demo/metrics', description: 'Get metrics' },
    { method: 'POST', path: '/api/v1/demo/task-suggestions', description: 'Get task suggestions' }
  ]

  const handleExecute = async () => {
    setLoading(true)
    // Simulate API call
    setTimeout(() => {
      setResponse(JSON.stringify({
        status: 'success',
        data: {
          message: 'API call simulated',
          timestamp: new Date().toISOString(),
          method,
          endpoint
        }
      }, null, 2))
      setLoading(false)
      message.success('Request executed successfully!')
    }, 1000)
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success('Copied to clipboard!')
  }

  return (
    <div style={{ 
      width: '100vw', 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: 0,
      margin: 0,
      overflow: 'auto'
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#ffffff' }}>
              API Playground
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Test and explore API endpoints
            </Text>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/fallbackapp/dashboard')}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#ffffff'
          }}
        >
          Back to Dashboard
        </Button>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px' }}>
          {/* Sidebar with Sample Endpoints */}
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              border: 'none',
              borderRadius: '12px',
              height: 'fit-content'
            }}
          >
            <Title level={5}>
              <HistoryOutlined /> Sample Endpoints
            </Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {sampleEndpoints.map((ep, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setMethod(ep.method)
                    setEndpoint(ep.path)
                  }}
                >
                  <Tag color={methodColors[ep.method]}>{ep.method}</Tag>
                  <div style={{ fontSize: '12px', marginTop: '4px' }}>
                    {ep.path}
                  </div>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {ep.description}
                  </Text>
                </div>
              ))}
            </Space>
          </Card>

          {/* Request/Response Area */}
          <div>
            {/* Request Builder */}
            <Card
              style={{
                marginBottom: '24px',
                background: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '12px'
              }}
            >
              <Title level={5}>Request</Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Select 
                    value={method} 
                    onChange={setMethod}
                    style={{ width: '120px' }}
                  >
                    {Object.keys(methodColors).map(m => (
                      <Option key={m} value={m}>
                        <Tag color={methodColors[m]}>{m}</Tag>
                      </Option>
                    ))}
                  </Select>
                  <Input 
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                    placeholder="Enter endpoint URL"
                    style={{ flex: 1 }}
                  />
                  <Button 
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleExecute}
                    loading={loading}
                    style={{
                      background: '#8b5cf6',
                      border: 'none'
                    }}
                  >
                    Execute
                  </Button>
                </div>

                <Tabs defaultActiveKey="body">
                  <TabPane tab="Body" key="body">
                    <TextArea
                      value={requestBody}
                      onChange={(e) => setRequestBody(e.target.value)}
                      placeholder="Enter request body (JSON)"
                      rows={8}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </TabPane>
                  <TabPane tab="Headers" key="headers">
                    <TextArea
                      placeholder="Enter headers (one per line)"
                      rows={8}
                      defaultValue="Content-Type: application/json\nAuthorization: Bearer token"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </TabPane>
                  <TabPane tab="Params" key="params">
                    <TextArea
                      placeholder="Enter query parameters"
                      rows={8}
                      style={{ fontFamily: 'monospace' }}
                    />
                  </TabPane>
                </Tabs>
              </Space>
            </Card>

            {/* Response */}
            <Card
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                border: 'none',
                borderRadius: '12px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Title level={5}>Response</Title>
                <Button 
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(response)}
                  disabled={!response}
                >
                  Copy
                </Button>
              </div>
              <div
                style={{
                  background: '#1e1e1e',
                  color: '#d4d4d4',
                  padding: '16px',
                  borderRadius: '6px',
                  minHeight: '200px',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all'
                }}
              >
                {response || 'Response will appear here...'}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FallbackAppApiPlayground